/**
 * Core Slider Component
 *
 * Range/value slider with marks, custom labels, min/max bounds, step,
 * range mode (two thumbs), and RBAC gating. Built on MUI Slider.
 *
 * @example
 *   <Slider label="Volume" value={50} onChange={setVol} />
 *   <Slider label="Price Range" range value={[20, 80]} onChange={setRange} min={0} max={100} step={5} />
 */
import React from 'react';
import { Slider as MuiSlider, Typography, Box } from '@mui/material';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface SliderMark {
  value: number;
  label?: string;
}

export interface SliderProps {
  /** Current value — number for single, [number, number] for range */
  value?: number | number[];
  /** Change handler */
  onChange?: (value: number | number[]) => void;
  /** Committed change (mouse up / blur) */
  onChangeCommitted?: (value: number | number[]) => void;

  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step size (null for continuous) */
  step?: number | null;
  /** Enable range mode (two thumbs) */
  range?: boolean;
  /** Display marks */
  marks?: boolean | SliderMark[];
  /** Label above the slider */
  label?: string;
  /** Show value label tooltip */
  valueLabelDisplay?: 'auto' | 'on' | 'off';
  /** Format value label text */
  valueLabelFormat?: (value: number) => string;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Color override */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /** Disabled state */
  disabled?: boolean;
  /** Orientation */
  orientation?: 'horizontal' | 'vertical';
  /** Custom track rendering */
  track?: 'normal' | 'inverted' | false;
  /** Accessible label */
  'aria-label'?: string;
  className?: string;
  /** RBAC permission gate */
  permission?: string;
}

export const Slider = React.forwardRef<HTMLSpanElement, SliderProps>(
  (
    {
      value,
      onChange,
      onChangeCommitted,
      min = 0,
      max = 100,
      step = 1,
      range = false,
      marks = false,
      label,
      valueLabelDisplay = 'auto',
      valueLabelFormat,
      size = 'medium',
      color = 'primary',
      disabled = false,
      orientation = 'horizontal',
      track = 'normal',
      className = '',
      permission,
      ...ariaProps
    },
    ref,
  ) => {
    const { user } = useAuth();
    if (permission && !hasPermission(user, permission)) return null;

    const defaultValue = range ? [min, max] : min;

    return (
      <Box className={className} sx={{ width: orientation === 'horizontal' ? '100%' : 'auto' }}>
        {label && (
          <Typography variant="body2" fontWeight={600} color="text.secondary" gutterBottom>
            {label}
          </Typography>
        )}
        <MuiSlider
          ref={ref}
          value={value ?? defaultValue}
          onChange={(_, val) => onChange?.(val as number | number[])}
          onChangeCommitted={(_, val) => onChangeCommitted?.(val as number | number[])}
          min={min}
          max={max}
          step={step}
          marks={marks}
          size={size}
          color={color}
          disabled={disabled}
          orientation={orientation}
          track={track}
          valueLabelDisplay={valueLabelDisplay}
          valueLabelFormat={valueLabelFormat}
          {...ariaProps}
        />
      </Box>
    );
  },
);
Slider.displayName = 'Slider';
export default Slider;
